import { getLCP, getCLS, getFID, getFCP, getTTFB, type Metric } from 'web-vitals';

// ---------------------------------------------------------------------------
// reportWebVitals — wires the five core Web Vitals (LCP, CLS, FID, FCP, TTFB)
// so we have real-user performance data to point at when a perf claim needs
// backing. Phase 3 backlog: LCP measurement on the briefing dashboard.
//
// Implementation notes:
//   * No external reporting endpoint yet — metrics log to the console in
//     development and POST to `/api/diagnostics/web-vitals` in production
//     if the backend is reachable. If the POST fails (e.g. offline), we
//     swallow silently because web-vitals is observational, not operational.
//   * Route context is captured from `window.location.pathname` so the
//     backend can segment LCP by briefing vs classic dashboard etc.
//   * "rating" (good / needs-improvement / poor) is derived client-side
//     because web-vitals 2.x does not ship it on the Metric type.
// ---------------------------------------------------------------------------

// Thresholds per https://web.dev/vitals/
const RATINGS: Record<string, [number, number]> = {
  LCP: [2500, 4000],
  FID: [100, 300],
  CLS: [0.1, 0.25],
  FCP: [1800, 3000],
  TTFB: [800, 1800],
};

function rate(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = RATINGS[name];
  if (!thresholds) return 'good';
  if (value <= thresholds[0]) return 'good';
  if (value <= thresholds[1]) return 'needs-improvement';
  return 'poor';
}

export interface WebVitalsReport {
  name: Metric['name'];
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  path: string;
}

function getApiBase(): string | null {
  const base = process.env.REACT_APP_API_URL ?? null;
  return base;
}

async function postMetric(report: WebVitalsReport): Promise<void> {
  const api = getApiBase();
  if (!api) return;
  try {
    await fetch(`${api}/diagnostics/web-vitals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
      keepalive: true,
    });
  } catch {
    // Observational only — never block or alert on a missed metric.
  }
}

function report(metric: Metric): void {
  const payload: WebVitalsReport = {
    name: metric.name,
    value: metric.value,
    rating: rate(metric.name, metric.value),
    delta: metric.delta,
    id: metric.id,
    path: window.location.pathname,
  };

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[web-vitals]', payload);
  }

  void postMetric(payload);
}

export function reportWebVitals(): void {
  // Called once on mount. web-vitals 2.x each take one callback.
  getLCP(report);
  getCLS(report);
  getFID(report);
  getFCP(report);
  getTTFB(report);
}

export default reportWebVitals;
